import { Helmet } from 'react-helmet-async';

interface PageMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

/**
 * PageMeta Component
 * 
 * Enterprise-grade SEO meta tag management for ADJUDON.
 * Follows Government-Grade standards with structured data support.
 * 
 * @param title - Page title (appends " | ADJUDON" automatically)
 * @param description - Meta description (150-160 chars recommended)
 * @param keywords - Comma-separated keywords for SEO
 * @param ogType - Open Graph type (website or article)
 * @param ogImage - Open Graph image URL (1200x630px recommended)
 * @param canonical - Canonical URL for duplicate content
 * @param noindex - Prevent search engine indexing (for private/beta pages)
 */
export function PageMeta({
  title = 'AI Governance & EU AI Act Compliance',
  description = 'ADJUDON provides enterprise-grade AI governance with runtime auditability, confidence scoring, and EU AI Act compliance. Built for regulated industries.',
  keywords = 'AI governance, EU AI Act, compliance, audit trail, confidence scoring, policy engine, LLM security, prompt injection, healthcare AI, fintech AI',
  ogType = 'website',
  ogImage = '/og-default.png',
  canonical,
  noindex = false,
}: PageMetaProps) {
  const fullTitle = title === 'AI Governance & EU AI Act Compliance' 
    ? 'ADJUDON | AI Governance & EU AI Act Compliance'
    : `${title} | ADJUDON`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="ADJUDON" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional Meta — `theme-color` per scheme so the browser
          chrome (Android address bar, Safari iOS status bar) matches
          whichever editorial theme is active. Without `media`, the
          browser caches the first value and never updates on toggle. */}
      <meta name="theme-color" media="(prefers-color-scheme: light)" content="#F8F7F4" />
      <meta name="theme-color" media="(prefers-color-scheme: dark)" content="#08090c" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    </Helmet>
  );
}
